import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: "customer", business_name: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await register(form);
    setBusy(false);
    if (!res.ok) { toast.error(res.error); return; }
    if (res.user.role === "vendor") {
      toast.success("Vendor account created. Awaiting admin approval.");
      nav("/vendor", { replace: true });
    } else {
      toast.success("Account created");
      nav("/dashboard", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-[#050B14] text-white">
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-16">
        <div className="bg-[#0A111A]/60 backdrop-blur-2xl border border-white/10 rounded-2xl p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Get started</p>
          <h1 className="text-3xl font-light tracking-tight mb-8">Create your account.</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider mb-3 block">I am a</Label>
              <RadioGroup value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} className="grid grid-cols-2 gap-3">
                <label data-testid="role-customer" className={`cursor-pointer rounded-lg border p-4 transition-colors ${form.role === "customer" ? "border-sky-400 bg-sky-400/10" : "border-white/10 hover:bg-white/5"}`}>
                  <RadioGroupItem value="customer" className="sr-only" />
                  <p className="font-medium text-white">Customer</p>
                  <p className="text-xs text-slate-400 mt-1">Rent vehicles for trips</p>
                </label>
                <label data-testid="role-vendor" className={`cursor-pointer rounded-lg border p-4 transition-colors ${form.role === "vendor" ? "border-sky-400 bg-sky-400/10" : "border-white/10 hover:bg-white/5"}`}>
                  <RadioGroupItem value="vendor" className="sr-only" />
                  <p className="font-medium text-white">Vendor</p>
                  <p className="text-xs text-slate-400 mt-1">List & rent out vehicles</p>
                </label>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-slate-300 text-xs uppercase tracking-wider">Full Name</Label>
                <Input id="name" required value={form.name} onChange={set("name")} data-testid="reg-name-input"
                  className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400 focus:ring-1 focus:ring-sky-400" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-300 text-xs uppercase tracking-wider">Phone</Label>
                <Input id="phone" value={form.phone} onChange={set("phone")} data-testid="reg-phone-input"
                  className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400 focus:ring-1 focus:ring-sky-400" />
              </div>
            </div>
            {form.role === "vendor" && (
              <div>
                <Label htmlFor="biz" className="text-slate-300 text-xs uppercase tracking-wider">Business Name</Label>
                <Input id="biz" value={form.business_name} onChange={set("business_name")} data-testid="reg-business-input"
                  className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400 focus:ring-1 focus:ring-sky-400" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="reg-email-input"
                className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400 focus:ring-1 focus:ring-sky-400" />
            </div>
            <div>
              <Label htmlFor="pw" className="text-slate-300 text-xs uppercase tracking-wider">Password</Label>
              <Input id="pw" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="reg-password-input"
                className="bg-[#050B14] border-white/10 text-white mt-2 focus:border-sky-400 focus:ring-1 focus:ring-sky-400" />
            </div>
            <Button type="submit" disabled={busy} data-testid="reg-submit-btn"
              className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-medium py-6">
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-slate-400 mt-6 text-center">
            Already have an account? <Link to="/login" data-testid="reg-go-login" className="text-sky-300 hover:text-sky-200">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
